import { z } from "zod";
import { TimelineEventSchema } from "../workflows/timeline";

export const QueueMemberSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    addedAt: z.string(),
});

export type QueueMember = z.infer<typeof QueueMemberSchema>;

export const QueueStatusSchema = z
    .enum(["active", "deactivating", "deactivated"])
    .default("active");

export type QueueStatus = z.infer<typeof QueueStatusSchema>;

export const QueueSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    isPersonal: z.boolean(),
    ownerUserId: z.string().nullable(),
    // Nullable to tolerate legacy rows that haven't been touched by the
    // startup backfill yet. Optional because pre-feature builds of this
    // service didn't include the field at all.
    ownerManagementGroupId: z.string().nullable().optional(),
    createdAt: z.string(),
    memberCount: z.number(),
    members: z.array(QueueMemberSchema),
    parentQueueId: z.string().nullable(),
    isWorkable: z.boolean(),
    depth: z.number(),
    ancestorPath: z.array(z.string()),
    // Caller-relative server verdict. Default false preserves compatibility
    // with list/tree payloads from versions that predate delegated queue
    // administration; detail responses always provide it.
    canAdminister: z.boolean().default(false),
    // Defaults preserve compatibility with queue payloads produced before
    // reversible deactivation was introduced.
    status: QueueStatusSchema,
    deactivationStartedAt: z.string().nullable().optional().default(null),
    deactivatedAt: z.string().nullable().optional().default(null),
});

export type QueueSummary = z.infer<typeof QueueSummarySchema>;

// Queue and workflow timelines share the Activity Ledger event envelope.
// Reuse the single boundary parser so the same wire record cannot acquire
// two interpretations in adjacent feature slices. Queue events never carry
// the optional work-item enrichment, but tolerating that field preserves the
// common renderer contract.
export const QueueTimelineResponseSchema = z.object({
    events: z.array(TimelineEventSchema),
    truncated: z.boolean(),
    partial: z.boolean(),
});

export type QueueTimelineResponse = z.infer<typeof QueueTimelineResponseSchema>;

export const QueueListResponseSchema = z.object({
    items: z.array(QueueSummarySchema),
});

export type QueueListResponse = z.infer<typeof QueueListResponseSchema>;

// ---------------------------------------------------------------------------
// Per-member push-notification status for one queue. Kept separate from
// QueueMemberSchema on purpose: the mute data has a different source (the
// Push preference store) than the queue aggregate, so it travels on its own
// read rather than widening the shared queue projection.
//
// `muted` defaults to false so a row from an older/leaner payload (or a user
// the backend omitted) reads as "notifications on" rather than throwing.
// ---------------------------------------------------------------------------

export const QueueMemberNotificationStatusSchema = z.object({
    userId: z.string(),
    muted: z.boolean().default(false),
});

export type QueueMemberNotificationStatus = z.infer<typeof QueueMemberNotificationStatusSchema>;

export const QueueNotificationStatusResponseSchema = z.object({
    items: z.array(QueueMemberNotificationStatusSchema),
});

export type QueueNotificationStatusResponse = z.infer<typeof QueueNotificationStatusResponseSchema>;

// Recursive tree projection: each node carries a queue plus its direct
// children. Zod requires the lazy() escape hatch for the self-reference.
export interface QueueTreeNode {
    readonly queue: QueueSummary;
    readonly children: readonly QueueTreeNode[];
}

interface QueueTreeNodeInput {
    readonly queue: z.input<typeof QueueSummarySchema>;
    readonly children: readonly QueueTreeNodeInput[];
}

export const QueueTreeNodeSchema: z.ZodType<QueueTreeNode, z.ZodTypeDef, QueueTreeNodeInput> =
    z.lazy(() =>
        z.object({
            queue: QueueSummarySchema,
            children: z.array(QueueTreeNodeSchema),
        }),
    );

export const QueueTreeResponseSchema = z.object({
    roots: z.array(QueueTreeNodeSchema),
});

export type QueueTreeResponse = z.infer<typeof QueueTreeResponseSchema>;

// ---------------------------------------------------------------------------
// Per-queue work-item tallies that back the queue-list indicator badges.
//
// `asOf` is the server-pinned instant the buckets were classified against,
// echoed back so a future SignalR push can include the same instant and let
// caches reconcile deterministically (Open / Pending are time-sensitive,
// unlike completed counts).
// ---------------------------------------------------------------------------

export const QueueWorkItemTallySchema = z.object({
    queueId: z.string(),
    open: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
});

export type QueueWorkItemTally = z.infer<typeof QueueWorkItemTallySchema>;

export const QueueWorkItemTalliesResponseSchema = z.object({
    tallies: z.array(QueueWorkItemTallySchema),
    asOf: z.string(),
});

export type QueueWorkItemTalliesResponse = z.infer<typeof QueueWorkItemTalliesResponseSchema>;

// ---------------------------------------------------------------------------
// HR User directory search (returned by /queues/users/search proxy).
//
// Mirrors Galileo.Hr.User.Client.SearchUsersResponse on the wire. Optional
// profile fields are nullable rather than absent because System.Text.Json
// emits explicit `null` for unset reference properties on `record` types.
// ---------------------------------------------------------------------------

export const UserProfileSchema = z.object({
    displayName: z.string().nullable().optional(),
    givenName: z.string().nullable().optional(),
    surname: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    userPrincipalName: z.string().nullable().optional(),
    jobTitle: z.string().nullable().optional(),
    department: z.string().nullable().optional(),
    officeLocation: z.string().nullable().optional(),
    usageLocation: z.string().nullable().optional(),
    employeeId: z.string().nullable().optional(),
    employeeType: z.string().nullable().optional(),
});

export type UserProfile = z.infer<typeof UserProfileSchema>;

export const UserSearchResultSchema = z.object({
    id: z.string(),
    classification: z.union([z.string(), z.number()]),
    lifecycleState: z.union([z.string(), z.number()]),
    createdAt: z.string(),
    mergedIntoUserId: z.string().nullable().optional(),
    profile: UserProfileSchema,
});

export type UserSearchResult = z.infer<typeof UserSearchResultSchema>;

export const UserSearchResponseSchema = z.object({
    results: z.array(UserSearchResultSchema),
});

export type UserSearchResponse = z.infer<typeof UserSearchResponseSchema>;
